import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Store } from 'lucide-react';

const HERO = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=85';

export default function BrandLogin() {
    const { loginBrand } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('brand.zara@inorbit.one');
    const [password, setPassword] = useState('Zara@2026');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await loginBrand(email, password);
            navigate('/brand');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invalid credentials');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="brand-login-page">
            {/* Left hero */}
            <div className="relative hidden lg:block bg-inorbit-blue-deeper overflow-hidden">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-br from-inorbit-blue/90 via-inorbit-blue-deeper/80 to-inorbit-blue-deeper" />
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] border-2 border-inorbit-yellow/15 rounded-full orbit-slow" />
                <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] border border-inorbit-orange/20 rounded-full orbit-fast" />
                <div className="relative h-full flex flex-col justify-between p-16 text-white">
                    <div>
                        <div className="flex items-center gap-3">
                            <img src="/inorbit-logo.png" alt="Inorbit" className="h-12 bg-white p-1 rounded" />
                            <div>
                                <div className="font-serif text-2xl">INORBIT</div>
                                <div className="text-[10px] tracking-[0.28em] text-inorbit-yellow font-semibold">ONE • BRAND PORTAL</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="overline text-inorbit-yellow mb-4">Tenant Operations</div>
                        <h1 className="font-serif text-5xl xl:text-6xl leading-[0.98] tracking-tight">
                            Manage your store<br />
                            <span className="script-tag text-inorbit-yellow text-6xl xl:text-7xl">across every Inorbit.</span>
                        </h1>
                        <p className="mt-8 text-white/80 leading-relaxed max-w-md">
                            Real-time orders. Live inventory. Customer 360. Bills captured at POS — all flowing into one unified brand console.
                        </p>
                    </div>
                    <div className="text-xs text-white/50 tracking-widest uppercase">© 2026 Inorbit Malls • K Raheja Corp</div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center px-6 lg:px-20 bg-white py-20">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="brand-login-form">
                    <div className="mb-12 lg:hidden">
                        <img src="/inorbit-logo.png" alt="Inorbit" className="h-12" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <Store size={18} className="text-inorbit-orange" strokeWidth={1.6} />
                        <div className="overline">Brand Portal</div>
                    </div>
                    <h2 className="font-serif text-5xl text-inorbit-blue leading-tight">Sign in</h2>
                    <p className="text-sm text-text-secondary mt-3">Tenant brand operations console.</p>
                    <div className="space-y-7 mt-10">
                        <div>
                            <label className="overline">Brand Email</label>
                            <input type="email" className="input-luxury" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="brand-email-input" />
                        </div>
                        <div>
                            <label className="overline">Password</label>
                            <input type="password" className="input-luxury" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="brand-password-input" />
                        </div>
                    </div>
                    <button type="submit" disabled={busy} className="btn-primary mt-10 w-full justify-center" data-testid="brand-login-btn">
                        {busy ? 'Signing in…' : 'Access Brand Console'}
                    </button>
                    <div className="text-xs text-text-tertiary mt-8 leading-relaxed border border-border-subtle rounded-md p-4 bg-cream">
                        <div className="overline mb-2 text-inorbit-orange">Demo Brand Accounts</div>
                        <div className="space-y-1 font-mono text-[11px]">
                            <div>brand.zara@inorbit.one / Zara@2026</div>
                            <div>brand.hm@inorbit.one / HnM@2026</div>
                            <div>brand.sephora@inorbit.one / Sephora@2026</div>
                            <div>brand.nykaa@inorbit.one / Nykaa@2026</div>
                            <div>brand.adidas@inorbit.one / Adidas@2026</div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
